import { useQuery } from "@tanstack/react-query"
import type { UseQueryResult, QueryFunctionContext } from "@tanstack/react-query"
import type { LedgerReportingJurisdiction } from "@/types/djangio"
import { ResponseNotOkError } from "@/api"

const queryKeys = {
    all: [{ _model: "ledgerreportingjurisdiction" }],
    list: (ledgerSettingsId: string) =>
        Object.freeze([Object.freeze({ ...queryKeys.all[0], entity: "list", ledgerSettingsId })]),
    detail: (id: string) => Object.freeze([Object.freeze({ ...queryKeys.all[0], id })]),
}

export const fetchLedgerReportingJurisdiction = async ({
    queryKey: [{ id }],
}: QueryFunctionContext<ReturnType<(typeof queryKeys)["detail"]>>): Promise<LedgerReportingJurisdiction> => {
    const response = await fetch(`/api/ledgerreportingjurisdiction/${id}/?decode_enums=false`)
    if (!response.ok) {
        throw new ResponseNotOkError(response)
    }
    return response.json()
}

export const fetchLedgerReportingJurisdictions = async ({
    queryKey: [{ ledgerSettingsId }],
}: QueryFunctionContext<ReturnType<(typeof queryKeys)["list"]>>): Promise<LedgerReportingJurisdiction[]> => {
    const response = await fetch(
        `/api/ledgerreportingjurisdiction/?decode_enums=false&ledger_settings_id=${ledgerSettingsId}&archived=false`,
    )
    if (!response.ok) {
        throw new ResponseNotOkError(response)
    }
    const parsedResponse = await response.json()

    return parsedResponse.objects
}

export const useLedgerReportingJurisdiction = (id: string): UseQueryResult<LedgerReportingJurisdiction> =>
    useQuery({
        queryKey: queryKeys.detail(id),
        queryFn: fetchLedgerReportingJurisdiction,
        enabled: Boolean(id),
    })

export const useLedgerReportingJurisdictions = (
    ledgerSettingsId: string,
): UseQueryResult<LedgerReportingJurisdiction[]> =>
    useQuery({
        queryKey: queryKeys.list(ledgerSettingsId),
        queryFn: fetchLedgerReportingJurisdictions,
        enabled: Boolean(ledgerSettingsId),
    })
